import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { z } from 'zod';
import { trpc } from '../../lib/trpc';

const searchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute('/household/invite')({
  validateSearch: searchSchema,
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const { token } = useSearch({ from: '/household/invite' });
  const navigate = useNavigate();

  const mutation = trpc.household.acceptInvite.useMutation({
    onSuccess: () => void navigate({ to: '/' }),
  });

  useEffect(() => {
    if (token) {
      mutation.mutate({ token });
    }
  }, [token, mutation.mutate]);

  if (!token) {
    return (
      <InviteCard icon="🔗" title="Ogiltig inbjudningslänk">
        Länken saknar en inbjudan. Be den som bjöd in dig om en ny länk.
      </InviteCard>
    );
  }

  if (mutation.error) {
    return (
      <InviteCard icon="⚠️" title="Inbjudan kunde inte accepteras">
        <span className="block text-negative">{mutation.error.message}</span>
        <span className="mt-1 block">Inbjudan kan ha gått ut eller redan accepterats.</span>
      </InviteCard>
    );
  }

  return (
    <InviteCard icon="🏡" title="Accepterar inbjudan…">
      Du läggs till i hushållet. Det tar bara ett ögonblick.
    </InviteCard>
  );
}

function InviteCard({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto mt-8 max-w-sm sm:mt-16">
      <div className="rounded-2xl border border-border bg-card p-6 text-center sm:p-7">
        <span
          aria-hidden
          className="mx-auto flex size-12 items-center justify-center rounded-full bg-muted text-2xl"
        >
          {icon}
        </span>
        <h1 className="mt-4 text-xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{children}</p>
      </div>
      <p className="mt-5 text-center text-sm">
        <Link to="/" className="font-medium text-primary hover:underline">
          Till Budgie
        </Link>
      </p>
    </div>
  );
}
